// Servicio de Configuraciones de Envío
// Maneja las configuraciones de envío desde Firestore

import { getDocument, setDocument } from './firestore.service'
import { parseNumberLoose } from '../utils/helpers'

const SHIPPING_SETTINGS_COLLECTION = 'shippingSettings'
const SHIPPING_SETTINGS_DOC_ID = 'main' // Un solo documento con todas las configuraciones

const computeEnabled = (fulfillment, distancePricing) => {
  const homeDeliveryEnabled = fulfillment?.homeDeliveryEnabled !== false
  const pickupEnabled = fulfillment?.pickupEnabled === true
  const negotiationEnabled = distancePricing?.negotiationEnabled === true || distancePricing?.negotiationEnabled === 'true'
  return homeDeliveryEnabled || pickupEnabled || negotiationEnabled
}

const normalizeFulfillment = (settings) => {
  const fulfillment = settings?.fulfillment && typeof settings.fulfillment === 'object' ? settings.fulfillment : null
  const homeDeliveryEnabled = fulfillment?.homeDeliveryEnabled !== undefined
    ? fulfillment.homeDeliveryEnabled !== false
    : settings?.enabled !== false
  const pickupEnabled = fulfillment?.pickupEnabled === true
  const pickupWarehouseIds = Array.isArray(fulfillment?.pickupWarehouseIds)
    ? fulfillment.pickupWarehouseIds.filter((x) => typeof x === 'string' && x.trim()).map((x) => x.trim())
    : []
  return { homeDeliveryEnabled, pickupEnabled, pickupWarehouseIds }
}

const normalizeDistancePricing = (settings) => {
  const distancePricing = settings?.distancePricing && typeof settings.distancePricing === 'object'
    ? settings.distancePricing
    : null
  const originAddress = distancePricing?.originAddress && typeof distancePricing.originAddress === 'object'
    ? {
      street: distancePricing.originAddress.street || '',
      apartment: distancePricing.originAddress.apartment || '',
      city: distancePricing.originAddress.city || '',
      postalCode: distancePricing.originAddress.postalCode || '',
      province: distancePricing.originAddress.province || '',
      country: distancePricing.originAddress.country || 'ARGENTINA'
    }
    : { street: '', apartment: '', city: '', postalCode: '', province: '', country: 'ARGENTINA' }
  const coords = distancePricing?.originCoordinates || null
  const originCoordinates = coords && Number.isFinite(Number(coords.lat)) && Number.isFinite(Number(coords.lng))
    ? { lat: Number(coords.lat), lng: Number(coords.lng) }
    : null
  const tiers = Array.isArray(distancePricing?.tiers)
    ? distancePricing.tiers
      .map((t) => ({
        maxDistance: Number(t?.maxDistance),
        cost: Number(t?.cost)
      }))
      .filter((t) => Number.isFinite(t.maxDistance) && t.maxDistance > 0 && Number.isFinite(t.cost) && t.cost >= 0)
      .sort((a, b) => a.maxDistance - b.maxDistance)
    : []
  const negotiationDistance = parseNumberLoose(distancePricing?.negotiationDistance)
  return {
    originAddress,
    originCoordinates,
    tiers,
    negotiationDistance: Number.isFinite(negotiationDistance) && negotiationDistance > 0 ? negotiationDistance : null,
    negotiationEnabled: distancePricing?.negotiationEnabled === true || distancePricing?.negotiationEnabled === 'true'
  }
}

/**
 * Obtener las configuraciones de envío
 * @returns {Promise<Object>} Configuraciones de envío
 */
export const getShippingSettings = async () => {
  try {
    const settings = await getDocument(SHIPPING_SETTINGS_COLLECTION, SHIPPING_SETTINGS_DOC_ID)
    
    // Si no existe, retornar valores por defecto
    if (!settings) {
      const fulfillment = normalizeFulfillment(null)
      const distancePricing = normalizeDistancePricing(null)
      return {
        freeShippingMinAmount: 50000, // Envío gratis a partir de $50.000
        freeShippingMaxDistance: null, // Sin límite de distancia por defecto
        defaultShippingCost: 15000, // Costo de envío por defecto
        enabled: computeEnabled(fulfillment, distancePricing),
        fulfillment,
        distancePricing
      }
    }
    
    const fulfillment = normalizeFulfillment(settings)
    const distancePricing = normalizeDistancePricing(settings)
    return {
      freeShippingMinAmount: settings.freeShippingMinAmount || 50000,
      freeShippingMaxDistance: settings.freeShippingMaxDistance || null,
      defaultShippingCost: settings.defaultShippingCost || 15000,
      enabled: computeEnabled(fulfillment, distancePricing),
      fulfillment,
      distancePricing
    }
  } catch (error) {
    console.error('Error obteniendo configuraciones de envío:', error)
    // Retornar valores por defecto en caso de error
    const fulfillment = normalizeFulfillment(null)
    const distancePricing = normalizeDistancePricing(null)
    return {
      freeShippingMinAmount: 50000,
      freeShippingMaxDistance: null,
      defaultShippingCost: 15000,
      enabled: computeEnabled(fulfillment, distancePricing),
      fulfillment,
      distancePricing
    }
  }
}

/**
 * Guardar las configuraciones de envío
 * @param {Object} settings - Configuraciones a guardar
 * @returns {Promise<void>}
 */
export const saveShippingSettings = async (settings) => {
  try {
    const fulfillmentInput = settings?.fulfillment && typeof settings.fulfillment === 'object' ? settings.fulfillment : null
    const distancePricingInput = settings?.distancePricing && typeof settings.distancePricing === 'object'
      ? settings.distancePricing
      : null
    const fulfillment = {
      homeDeliveryEnabled: fulfillmentInput?.homeDeliveryEnabled !== false,
      pickupEnabled: fulfillmentInput?.pickupEnabled === true,
      pickupWarehouseIds: Array.isArray(fulfillmentInput?.pickupWarehouseIds)
        ? fulfillmentInput.pickupWarehouseIds.filter((x) => typeof x === 'string' && x.trim()).map((x) => x.trim())
        : []
    }
    const originAddress = distancePricingInput?.originAddress && typeof distancePricingInput.originAddress === 'object'
      ? {
        street: distancePricingInput.originAddress.street || '',
        apartment: distancePricingInput.originAddress.apartment || '',
        city: distancePricingInput.originAddress.city || '',
        postalCode: distancePricingInput.originAddress.postalCode || '',
        province: distancePricingInput.originAddress.province || '',
        country: distancePricingInput.originAddress.country || 'AR'
      }
      : { street: '', apartment: '', city: '', postalCode: '', province: '', country: 'AR' }
    const coords = distancePricingInput?.originCoordinates || null
    const originCoordinates = coords && Number.isFinite(Number(coords.lat)) && Number.isFinite(Number(coords.lng))
      ? { lat: Number(coords.lat), lng: Number(coords.lng) }
      : null
    const tiers = Array.isArray(distancePricingInput?.tiers)
      ? distancePricingInput.tiers
        .map((t) => ({
          maxDistance: parseFloat(t?.maxDistance),
          cost: parseFloat(t?.cost)
        }))
        .filter((t) => Number.isFinite(t.maxDistance) && t.maxDistance > 0 && Number.isFinite(t.cost) && t.cost >= 0)
        .sort((a, b) => a.maxDistance - b.maxDistance)
      : []
    const negotiationDistance = parseNumberLoose(distancePricingInput?.negotiationDistance)
    const distancePricing = {
      originAddress,
      originCoordinates,
      tiers,
      negotiationDistance: Number.isFinite(negotiationDistance) && negotiationDistance > 0 ? negotiationDistance : null,
      negotiationEnabled: distancePricingInput?.negotiationEnabled === true || distancePricingInput?.negotiationEnabled === 'true'
    }

    const settingsData = {
      freeShippingMinAmount: parseFloat(settings.freeShippingMinAmount) || 0,
      freeShippingMaxDistance: settings.freeShippingMaxDistance ? parseFloat(settings.freeShippingMaxDistance) : null,
      defaultShippingCost: parseFloat(settings.defaultShippingCost) || 0,
      enabled: computeEnabled(fulfillment, distancePricing),
      fulfillment,
      distancePricing
    }
    
    await setDocument(SHIPPING_SETTINGS_COLLECTION, SHIPPING_SETTINGS_DOC_ID, settingsData, { merge: true })
  } catch (error) {
    console.error('Error guardando configuraciones de envío:', error)
    throw error
  }
}

export default {
  getShippingSettings,
  saveShippingSettings
}

